import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { Button, KIND as ButtonKind } from 'baseui/button'
import { useTranslation } from 'react-i18next'
import {
  selectClientCallbacksState,
} from './ClientCallbacksSelectors'
import {
  setView,
  setPage,
  openDeleteConfirm,
  closeDeleteConfirm,
} from './ClientCallbacksSlice'
import { clientCallbacksActions } from './ClientCallbacksSagas'
import useBusinessType from '@/shared/hooks/useBusinessType'

const useClientCallbacksPage = () => {
  const dispatch = useDispatch()
  const {
    items,
    loading,
    pagination,
    view,
    confirmDeleteId,
    deletingId,
  } = useSelector(selectClientCallbacksState)
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()
  const { translateBusinessTypeToUrl } = useBusinessType({})

  const fetchCallbacks = (page) => {
    dispatch({ type: clientCallbacksActions.FETCH, view, page, search: searchTerm })
  }

  useEffect(() => {
    fetchCallbacks(pagination.currentPage)
  }, [view, pagination.currentPage])

  const triggerSearch = () => {
    if (pagination.currentPage !== 1) {
      dispatch(setPage(1))
      return
    }
    fetchCallbacks(1)
  }

  const confirmDelete = () => {
    dispatch({ type: clientCallbacksActions.DELETE, id: confirmDeleteId, view, page: pagination.currentPage })
  }

  const columns = useMemo(() => {
    const base = [
      t('clientCallbacks:columns.scheduled_at'),
      t('clientCallbacks:columns.bs'),
      t('clientCallbacks:columns.name'),
      t('clientCallbacks:columns.phone'),
      t('clientCallbacks:columns.zoho_contact_id'),
    ]
    if (view === 'deleted') {
      base.push(t('clientCallbacks:columns.deleted_at'))
    }
    if (view === 'upcoming') {
      base.push(t('clientCallbacks:columns.action'))
    }
    return base
  }, [view])

  const createSubscriptionCell = (row) => {
    if (!row.subscriptionId) return row.subscriptionId || '-'

    const businessSlug = translateBusinessTypeToUrl(row.businessType || '')
    return (
      <a
        key={'bs-' + row.id}
        href={`/${businessSlug}/subscription/edit/${row.subscriptionId}`}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: '#276EF1' }}
      >
        {row.subscriptionId || '-'}
      </a>
    )
  }

  const createDeleteButton = (rowId) => (
    <Button
      key={'del-' + rowId}
      size='mini'
      kind={ButtonKind.tertiary}
      onClick={() => dispatch(openDeleteConfirm(rowId))}
      isLoading={deletingId === rowId}
      disabled={deletingId === rowId}
    >
      {t('clientCallbacks:delete_button')}
    </Button>
  )

  const createBaseRow = (row) => {
    const fullName = `${row.contactFirstName || ''} ${row.contactLastName || ''}`.trim() || '-'
    return [
      row.scheduledAt || '-',
      createSubscriptionCell(row),
      fullName,
      row.contactPhone || '-',
      row.zohoContactId || '-',
    ]
  }

  const mapRowToData = (row) => {
    const baseRow = createBaseRow(row)
    
    if (view === 'deleted') {
      baseRow.push(row.deletedAt || '-')
    }
    
    if (view === 'upcoming') {
      baseRow.push(createDeleteButton(row.id))
    }
    
    return baseRow
  }

  const data = items.map(mapRowToData)

  const refresh = () => fetchCallbacks(pagination.currentPage)
  const changePage = nextPage => dispatch(setPage(nextPage))
  const changeView = nextView => dispatch(setView(nextView))
  const closeDelete = () => dispatch(closeDeleteConfirm())

  return {
    t,
    items,
    loading,
    pagination,
    view,
    confirmDeleteId,
    searchTerm,
    columns,
    data,
    setSearchTerm,
    triggerSearch,
    refresh,
    changePage,
    changeView,
    confirmDelete,
    closeDelete,
  }
}

export default useClientCallbacksPage
